import { useCallback, useMemo } from 'react';
import {
  ApiBaseQueryError,
  CollectionFilteredModel,
  CollectionModel,
  customerApi,
  QueryTag,
} from '../api';
import { useGetConfigurationGroupsQuery } from './configurationGroupsApi';
import { useTableData } from '@customer/shared/hooks/useTableData';
import { FaultLevel, GlobalTimeRange } from '../slices/globalSlice';
import {
  AssetInteractionListModel,
  GIListModel,
  InteractionsSummaryModel,
  VIListModel,
} from './interactionsApi';
import { CustomFilterParams, getUrlParams, QueryParamNames, QueryParams } from './helpers';
import { NoteModel, PostNoteQuery } from './commonModels';
import { FiltersState } from '../slices/filtersSlice';
import { DocumentFamily, DocumentProduct, TechLibraryDocumentType } from '@cas/config/documents';
import { useAppSelector } from '../hooks';
import { ObjectTypeId } from '@dmc/ui';

const convertLevelNumToFaultLevel = (lvl: 1 | 2 | 3) => {
  switch (lvl) {
    case 1:
      return FaultLevel.One;
    case 2:
      return FaultLevel.Two;
    case 3:
      return FaultLevel.Three;
  }
};

export enum ReportingStatus {
  Good = 'Good',
  Bad = 'Bad',
  Moderate = 'Moderate',
}

export enum FileStatus {
  Unknown = 'Unknown',
  Downloading = 'Downloading',
  Failed = 'Failed',
  Executing = 'Executing',
  Ready = 'Ready',
  MismatchWithConfig = 'MismatchWithConfig',
  MissingFromDevice = 'MissingFromDevice',
  MissingFromConfig = 'MissingFromConfig',
}

export enum AssetListScenarios {
  HealthSelfTest = 'HealthSelfTest',
  HealthConfigStatus = 'HealthConfigStatus',
  HealthNameMismatch = 'HealthNameMismatch',
  HealthReporting = 'HealthReporting',
  PerformanceVehicleInteractions = 'PerformanceVehicleInteractions',
  PerformanceGeofenceInteractions = 'PerformanceGeofenceInteractions',
}

export type AssetListHealthScenario = Extract<
  AssetListScenarios,
  'HealthSelfTest' | 'HealthConfigStatus' | 'HealthNameMismatch' | 'HealthReporting'
>;

export type AssetListPerformanceScenario = Extract<
  AssetListScenarios,
  'PerformanceVehicleInteractions' | 'PerformanceGeofenceInteractions'
>;

export interface AssetBasicModel {
  id: number;
  name: string; // Asset Name (Cas Web / GPS gate)
  dn: string; // Asset Name (stored in the device, used for asset name mismatch)
  desc: string; // Asset Description (stored in device, formlery VIN)
  obj: ObjectTypeId; // Object type
  did: string; // Device ID
  hw: string; // Hardware Type / Device Type
  cfgGrp?: number; // Configuration Group
  configGroupName?: string; // Not from API - calculated optionally
  combCfgSts: boolean; // Software Status (Software/config status, Geofence status, name mismatch status)
  casSw: string; // Cas Software / Device Software
  ts?: string; // Timestamp (last report time)
  ags: number[]; // Asset Groups / Vehicle Groups / Tags
  stsCfg: boolean; // Software Status / Configuration Status
  stsRep?: ReportingStatus; // Reporting Status
  totFlt?: number; // Total faults
  totViAe?: number; // Total Vehicle Interactions - Aware
  totViAt?: number; // Total Vehicle Interactions - Alert
  totViAm?: number; // Total Vehicle Interactions - Alarm
  totVi?: number; // Not from API - calculated optionally
  totGi?: number; // Total Geofence Interactions
}

export interface TechLibraryDocumentFilterModel {
  documentType: TechLibraryDocumentType[]; // Not plural for backward compat.
  prods: DocumentProduct[];
  fams: DocumentFamily[];
}

export interface AssetComponentModel {
  type: string;
  hwRev?: string;
  serNo?: string;
  bootVer?: string;
  firmVer?: string;
  compId?: string;
  docFlt?: TechLibraryDocumentFilterModel;
}

export interface AssetFileModel {
  name: string;
  ver: string;
  rep: string;
  act: string;
  app: boolean;
  stat: FileStatus;
}

export interface PositionModelRelative {
  bg: number; // Relative bearing degrees (from forward of LO)
  bgEr: number; // Relative bearing error - degrees
  rd: number; // Relative distance - metres (from Y Rot offset)
  rdEr: number; // Relative distance error - metres
}

export interface PositionModel<T extends number | null = number | null> {
  ts: string; // GPS time of last report received.
  lat: T; // number | null;
  lon: T; // number | null;
  hd: number | null; // heading
  rel?: PositionModelRelative; // ToF-like relative position. Omitted from payload when null
}

export const isPositionModelWithLatLng = (
  model?: PositionModel | null,
): model is PositionModel<number> => {
  return !!model && model.lat !== null && model.lon !== null;
};

export interface AssetModel extends AssetBasicModel {
  osVer: string;
  geoVer: number;
  geoTs?: string;
  siteGeoVer: number;
  siteGeoTs?: string;
  components: AssetComponentModel[];
  files: AssetFileModel[];
  stsGeo: boolean;
  stsNam: boolean;
  stsFltL1: boolean; // Any active level 1 faults
  stsViOrGi: boolean; // Any reportable VI or GIs in the selected time range
  dn: string; // Name in the device
  tsRx?: string; // CAS Web (server) time of last message received.
  tsCfg: string;
  posG?: PositionModel; // Last good position/time
  posS?: PositionModel; // Position/Time last seen by another asset
  docFlt?: TechLibraryDocumentFilterModel;
  inst?: string; // This is actually JSON but we don't need to parse it out
}

export interface AssetFaultBasicModel {
  lvl: number;
  cat: number;
  flt: number;
}

export interface AssetFaultModel extends AssetFaultBasicModel {
  ts: string;
  dur: number; // seconds
  act: boolean;
}

export type InteractionScenarios =
  | AssetListScenarios.PerformanceVehicleInteractions
  | AssetListScenarios.PerformanceGeofenceInteractions;

export type AssetVIFilters = Partial<
  Pick<
    FiltersState,
    | 'assetGroupsRo'
    | 'audible'
    | 'gpsFix'
    | 'maxDistance'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minSpeedRo'
    | 'vehicleInteractionTypes'
  >
>;

export interface AssetVIQueryParams
  extends AssetVIFilters,
    Partial<CustomFilterParams>,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.PerformanceVehicleInteractions;
  timeRange?: QueryParams['timeRange'];
}

export type AssetGIFilters = Partial<
  Pick<
    FiltersState,
    | 'audible'
    | 'geofenceInteractionTypes'
    | 'gpsFix'
    | 'maxSpeedLo'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minTravelledDistance'
    | 'timeOfDayEnd'
    | 'timeOfDayStart'
  >
>;

export interface AssetGIQueryParams
  extends AssetGIFilters,
    Partial<CustomFilterParams>,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.PerformanceGeofenceInteractions;
  timeRange?: QueryParams['timeRange'];
}

export interface AssetInteractionQueryModel<T extends AssetGIQueryParams | AssetVIQueryParams> {
  assetId: string;
  query: T;
}

export interface AssetSelfTestCategorySummaryModel {
  cat: number;
  act: number;
  tot: number;
  dur: number; // seconds
}

export interface AssetSelfTestLevelSummaryModel {
  lvl: FaultLevel;
  tot: number;
  act: number;
  dur: number; // seconds
  cats: AssetSelfTestCategorySummaryModel[];
}

export interface AssetSelfTestSummaryModel {
  ign: number;
  lvls: AssetSelfTestLevelSummaryModel[];
}
interface AssetSelfTestLevelSummaryResponseModel
  extends Omit<AssetSelfTestLevelSummaryModel, 'lvl'> {
  lvl: 1 | 2 | 3;
}

export interface AssetSelfTestSummaryResponseModel {
  ign: number;
  lvls: AssetSelfTestLevelSummaryResponseModel[];
}

export enum DeviceType {
  Unknown = 'Unknown',
  X1 = 'X1',
  X9 = 'X9',
  IVU = 'IVU',
  IVUPlus = 'IVUPlus',
  PPU = 'PPU',
}

export enum FaultStatus {
  All = '0', // Don't send this to the API... just don't send the parameter
  Failed = '1',
  Active = '2',
}

// These values are converted to true/false before being sent as API query params.
export enum YesNo {
  All = '2', // Don't send this to the API... just don't send the parameter
  Yes = '1',
  No = '0',
}

export enum SoftwareStatus {
  All = '0', // Don't send this to the API... just don't send the parameter
  OutOfDate = '1',
  UpToDate = '2',
}

export enum GpsFix {
  All = '2', // Don't send this to the API... just don't send the parameter
  Good = '1',
  Invalid = '0',
}

// Create a subset of the filters that the other AssetsList interfaces can reference.
export type AssetsListFilters = Partial<
  Pick<
    FiltersState,
    | 'assetGroups'
    | 'assetGroupsRo'
    | 'audible'
    | 'configGroups'
    | 'deviceTypes'
    | 'faultCategories'
    | 'faults'
    | 'geofenceInteractionTypes'
    | 'gpsFix'
    | 'maxDistance'
    | 'maxSpeedLo'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minSpeedRo'
    | 'minTravelledDistance'
    | 'objectTypes'
    | 'objectTypesRo'
    | 'softwareStatus'
    | 'timeOfDayEnd'
    | 'timeOfDayStart'
    | 'vehicleInteractionTypes'
  >
>;

// Entire Site Assets List
export type SiteAssetsListFilters = Partial<
  Pick<AssetsListFilters, 'assetGroups' | 'configGroups' | 'deviceTypes' | 'softwareStatus'>
>;

// Scenario: HealthSoftwareStatus
export type HealthSoftwareStatusAssetsFilters = Partial<
  Pick<AssetsListFilters, 'assetGroups' | 'configGroups' | 'deviceTypes'>
>;

export interface HealthSoftwareStatusAssetsQueryParams
  extends HealthSoftwareStatusAssetsFilters,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.HealthConfigStatus;
}

// Scenario: HealthReporting
export type HealthReportingAssetsFilters = Partial<
  Pick<AssetsListFilters, 'assetGroups' | 'configGroups' | 'deviceTypes'>
>;

export interface HealthReportingAssetsQueryParams
  extends HealthReportingAssetsFilters,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.HealthReporting;
}

// Scenario: HealthAssetNameMismatch (has no filters)
export interface HealthMismatchAssetsQueryParams extends Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.HealthNameMismatch;
}

// Scenario: HealthSelfTest
export type HealthSelfTestAssetsFilters = Partial<
  Pick<AssetsListFilters, 'assetGroups' | 'deviceTypes' | 'faultCategories' | 'faults'>
>;

export interface HealthSelfTestAssetsQueryParams
  extends HealthSelfTestAssetsFilters,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.HealthSelfTest;
  timeRange?: GlobalTimeRange;
}

// Scenario: PerformanceVehicleInteractions
export type PerformanceVIAssetsFilters = Partial<
  Pick<
    AssetsListFilters,
    | 'assetGroups'
    | 'assetGroupsRo'
    | 'audible'
    | 'deviceTypes'
    | 'gpsFix'
    | 'maxDistance'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minSpeedRo'
    | 'objectTypes'
    | 'objectTypesRo'
    | 'vehicleInteractionTypes'
  >
>;

export interface PerformanceVIAssetsQueryParams
  extends PerformanceVIAssetsFilters,
    Partial<CustomFilterParams>,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.PerformanceVehicleInteractions;
  timeRange?: QueryParams['timeRange'];
}

// Scenario: PerformanceGeofenceInteractions
export type PerformanceGIAssetsFilters = Partial<
  Pick<
    AssetsListFilters,
    | 'assetGroups'
    | 'audible'
    | 'deviceTypes'
    | 'geofenceInteractionTypes'
    | 'gpsFix'
    | 'maxSpeedLo'
    | 'minDuration'
    | 'minSpeedLo'
    | 'minTravelledDistance'
    | 'objectTypes'
    | 'timeOfDayEnd'
    | 'timeOfDayStart'
  >
>;

export interface PerformanceGIAssetsQueryParams
  extends PerformanceGIAssetsFilters,
    Partial<CustomFilterParams>,
    Pick<QueryParams, 'scenario'> {
  scenario: AssetListScenarios.PerformanceGeofenceInteractions;
  timeRange?: QueryParams['timeRange'];
}

export type AssetsListFilterParams =
  | SiteAssetsListFilters
  | HealthSoftwareStatusAssetsFilters
  | HealthReportingAssetsFilters
  | HealthSelfTestAssetsFilters
  | PerformanceVIAssetsFilters
  | PerformanceGIAssetsFilters;

// Create a union of all the allowed query params for the AssetsList query.
export type AssetsListQueryParams =
  | SiteAssetsListFilters
  | HealthSoftwareStatusAssetsQueryParams
  | HealthReportingAssetsQueryParams
  | HealthMismatchAssetsQueryParams
  | HealthSelfTestAssetsQueryParams
  | PerformanceVIAssetsQueryParams
  | PerformanceGIAssetsQueryParams;

export interface AssetsListQueryModel {
  query: AssetsListQueryParams;
}

export interface AssetSelfTestQueryParams
  extends Partial<Pick<QueryParams, 'faultCategories' | 'faultLevels' | 'faults' | 'faultStatus'>> {
  timeRange: GlobalTimeRange;
}

export interface AssetSelfTestQueryModel {
  assetId: string;
  query: AssetSelfTestQueryParams;
}

export interface AssetList {
  error?: ApiBaseQueryError;
  assets: AssetBasicModel[];
  countAll?: number;
  countFilter?: number;
  countScenarioFilter?: number;
  refetch: () => void;
  isFetching: boolean;
  hasData: boolean;
  noDataDescription: string;
}

export interface SummaryQueryParams {
  timeRange: QueryParams['timeRange'];
}

type SummaryQueryType = AssetVIQueryParams | AssetGIQueryParams | SummaryQueryParams;

interface AssetInteractionsSummaryQueryModel {
  assetId: string;
  query: SummaryQueryType;
}

interface AssetActivityRequest {
  assetId: string;
  investigationId?: number;
}

const getAssetActivityParams = (investigationId?: number) => {
  if (investigationId !== undefined) {
    const queryParams = new URLSearchParams();
    queryParams.append(QueryParamNames.InvestigationId, investigationId.toString());
    return queryParams;
  }

  return undefined;
};

const isScenarioSummary = (
  query: SummaryQueryType,
): query is Extract<SummaryQueryType, { scenario: AssetListScenarios }> => {
  return 'scenario' in query;
};

export const SystemTypePpu = 'PPU';

export const assetsApi = customerApi.injectEndpoints({
  endpoints: build => {
    const listAssetInteractionsQueryFactory = <
      TResponse extends AssetInteractionListModel,
      TQP extends AssetGIQueryParams | AssetVIQueryParams,
    >(
      queryTag: QueryTag.AssetVehicleInteractions | QueryTag.AssetGeofenceInteractions,
    ) => {
      return build.query<CollectionFilteredModel<TResponse>, AssetInteractionQueryModel<TQP>>({
        query: ({ assetId, query }) => ({
          url: `api/assets/${assetId}/interactions`,
          params: getUrlParams(query),
        }),
        providesTags: (result, error, { assetId }) =>
          result ? [QueryTag.SiteCache, { type: queryTag, id: assetId }] : [],
      });
    };

    return {
      listAssets: build.query<CollectionFilteredModel<AssetBasicModel>, AssetsListQueryModel>({
        query: ({ query }) => ({
          url: 'api/assets',
          params: getUrlParams(query),
        }),
        // TODO: Should a unique id be provided for the different scenarios?
        providesTags: (result, error, arg) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.Asset, id: 'LIST' }] : [],
      }),
      getSingleAssets: build.query<AssetModel, string>({
        query: assetId => `api/assets/${assetId}`,
        providesTags: (result, error, assetId) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.Asset, id: assetId }] : [],
      }),
      listAssetFaults: build.query<
        CollectionFilteredModel<AssetFaultModel>,
        AssetSelfTestQueryModel
      >({
        query: ({ assetId, query }) => ({
          url: `api/assets/${assetId}/faults`,
          params: getUrlParams(query),
        }),
        providesTags: (result, error, { assetId }) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.AssetFaults, id: assetId }] : [],
      }),
      listAssetVehicleInteractions: listAssetInteractionsQueryFactory<
        VIListModel,
        AssetVIQueryParams
      >(QueryTag.AssetVehicleInteractions),
      listAssetGeofenceInteractions: listAssetInteractionsQueryFactory<
        GIListModel,
        AssetGIQueryParams
      >(QueryTag.AssetGeofenceInteractions),
      getAssetSelfTestSummary: build.query<AssetSelfTestSummaryModel, AssetSelfTestQueryModel>({
        query: ({ assetId, query }) => ({
          url: `api/assets/${assetId}/selftestsummary`,
          params: getUrlParams(query),
        }),
        // Note: The sole purpose of this transform is to convert lvl numbers into FaultLevel named constants.
        transformResponse: (
          response: AssetSelfTestSummaryResponseModel,
          meta,
          arg,
        ): AssetSelfTestSummaryModel => {
          const lvls = response.lvls.map((item: AssetSelfTestLevelSummaryResponseModel) => ({
            ...item,
            lvl: convertLevelNumToFaultLevel(item.lvl),
          }));

          return { ...response, lvls };
        },
        providesTags: (result, error, arg) =>
          result
            ? [QueryTag.SiteCache, { type: QueryTag.AssetSelfTestSummary, id: arg.assetId }]
            : [],
      }),
      getAssetInteractionsSummary: build.query<
        InteractionsSummaryModel,
        AssetInteractionsSummaryQueryModel
      >({
        query: ({ assetId, query }) => ({
          url: `api/assets/${assetId}/interactionssummary`,
          params: getUrlParams(query),
        }),
        // For caching optimisation, provide a different id based on the provided scenario query param (or its absence).
        providesTags: (result, error, arg) =>
          result
            ? [
                QueryTag.SiteCache,
                {
                  type: QueryTag.AssetInteractionsSummary,
                  id: isScenarioSummary(arg.query)
                    ? `${arg.assetId}--${arg.query.scenario}`
                    : arg.assetId,
                },
              ]
            : [],
      }),
      postAssetActivityNote: build.mutation<void, PostNoteQuery>({
        query: ({ id, ...body }) => ({
          url: `api/assets/${id}/notes`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (result, error, { id }) =>
          !error ? [{ type: QueryTag.AssetActivity, id: id }] : [],
      }),
      getAssetActivity: build.query<CollectionModel<NoteModel>, AssetActivityRequest>({
        query: ({ assetId, investigationId }) => ({
          url: `api/assets/${assetId}/activity`,
          params: getAssetActivityParams(investigationId),
        }),
        providesTags: (result, error, { assetId }) =>
          result ? [QueryTag.SiteCache, { type: QueryTag.AssetActivity, id: assetId }] : [],
      }),
    };
  },
  overrideExisting: false,
});

export const {
  useListAssetsQuery,
  useGetSingleAssetsQuery,
  useListAssetFaultsQuery,
  useListAssetVehicleInteractionsQuery,
  useListAssetGeofenceInteractionsQuery,
  useGetAssetSelfTestSummaryQuery,
  useGetAssetInteractionsSummaryQuery,
  usePostAssetActivityNoteMutation,
  useGetAssetActivityQuery,
} = assetsApi;

export const useListAssets = (query: AssetsListQueryModel, shouldSkip = false): AssetList => {
  const siteId = useAppSelector(state => state.auth.siteId);

  const listAssetsResponse = useListAssetsQuery(query, {
    refetchOnMountOrArgChange: 60,
    skip: typeof siteId === 'undefined' || shouldSkip,
  });

  const {
    data: listAssetsData,
    error: fetchError,
    refetch: refetchListAssets,
  } = listAssetsResponse;

  const { items: assets, countAll, countFilter } = listAssetsData ?? {};
  const countScenarioFilter = assets?.length;

  const configGroupsResponse = useGetConfigurationGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const {
    data: configGroups,
    error: configGroupsError,
    refetch: refetchGetConfigurationGroups,
  } = configGroupsResponse;

  const mergedAssets = useMemo<AssetBasicModel[]>(() => {
    if (assets && configGroups) {
      return assets.map(asset => {
        return {
          ...asset,
          configGroupName: configGroups.find(c => c.id === asset.cfgGrp)?.name ?? '',
          totVi:
            asset.totViAm !== undefined &&
            asset.totViAt !== undefined &&
            asset.totViAe !== undefined
              ? asset.totViAm + asset.totViAt + asset.totViAe
              : undefined,
        };
      });
    }

    return [];
  }, [assets, configGroups]);

  const refetch = useCallback(() => {
    refetchListAssets();
    refetchGetConfigurationGroups();
  }, [refetchListAssets, refetchGetConfigurationGroups]);

  const isUninitialized =
    listAssetsResponse.isUninitialized || configGroupsResponse.isUninitialized;
  const isLoading = listAssetsResponse.isLoading || configGroupsResponse.isLoading;
  const isFetching = listAssetsResponse.isFetching || configGroupsResponse.isFetching;
  const isSuccess = listAssetsResponse.isSuccess && configGroupsResponse.isSuccess;
  const isError = listAssetsResponse.isError || configGroupsResponse.isError;

  const { hasData, noDataDescription } = useTableData<AssetBasicModel>({
    data: mergedAssets,
    isUninitialized,
    isLoading,
    isFetching,
    isSuccess,
    isError,
  });

  return {
    error: fetchError || configGroupsError,
    assets: mergedAssets,
    countAll,
    countFilter,
    countScenarioFilter,
    isFetching,
    refetch,
    hasData,
    noDataDescription,
  };
};
